import {
  CommandBarButton,
  DirectionalHint,
  IconButton,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import React, { useEffect, useState } from 'react';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { FilterArrayType } from 'common/components/Filters';
import { useCommonStyles } from 'common/styles';
import { ApprovalFilter } from 'common/types/globalTypes';
import { SortOrder } from 'common/utils/commonTypes';
import { toOrderByVariable } from 'settings/account/approvalSetup/list/utils';
import {
  ApprovalsInfo,
  ApprovalsInfoVariables,
} from '../__generated__/ApprovalsInfo';
import { ReorderHierarchy } from '../Hierarchy';
import { ApprovalTypeFilter } from './ApprovalType';
import { ProjectPhase } from './ProjectPhase';
import { KEY_ALL, toPaymentFilterVariable } from './utils';
import { ApprovalFlowDesigner } from '../ApprovalFlowDesigner';
import queryString from 'query-string';

import { useHistory, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';

import GET_APPROVAL_DETAILS from '../ApprovalsInfo.graphql';
import {
  ActiveApprovalTypes,
  ActiveApprovalTypesVariables,
} from './__generated__/ActiveApprovalTypes';
import ACTIVE_APPROVAL_TYPES from './ActiveApprovalTypes.graphql';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { expandedNav } from 'utility/cache/ui';

export const ApprovalSetupFlowList: React.FC = () => {
  const commonStyles = useCommonStyles();
  const isExpandedNav = useReactiveVar(expandedNav);
  const [sortOrderParam] = useState<SortOrder>();
  const [projectPhase, setProjectPhase] = useState<string | undefined>(KEY_ALL);
  const [approvalType, setApprovalType] = useState<number | undefined>(
    undefined
  );
  const EXECUTION_PRIORITY_HINT_TEXT =
    'The execution priority determines the order in which the system selects an approval flow when someone submits a request for transaction approval. The approval engine starts at the top of the list and works its way down until it finds a match based on the defined conditions and approval level rules. If no rules or conditions match any authorized approval flow, an error message is displayed to the requester, indicating that there are not enough approvers to satisfy the request.';
  const history = useHistory();
  const location = useLocation();
  const { id } = queryString.parse(location.search);
  const [searchFilters] = useState<FilterArrayType[]>([]);
  const [
    getApprovalInfo,
    { data: approvalsInfo, loading: approvalInfoLoading, refetch, variables },
  ] = useLazyQuery<ApprovalsInfo, ApprovalsInfoVariables>(
    GET_APPROVAL_DETAILS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-and-network',
    }
  );
  const [getApprovalTypes, { data }] = useLazyQuery<
    ActiveApprovalTypes,
    ActiveApprovalTypesVariables
  >(ACTIVE_APPROVAL_TYPES, {
    variables: {
      isAllPhases: false,
    },
    onCompleted(data) {
      setApprovalType(data?.activeApprovalTypes?.nodes[0]?.id);
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const refreshApprovalTypes = () => {
    if (projectPhase !== KEY_ALL) {
      getApprovalTypes({
        variables: { phaseTypeId: Number(projectPhase) },
      });
    } else {
      getApprovalTypes({
        variables: { isAllPhases: true },
      });
    }
  };
  useEffect(() => {
    refreshApprovalTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPhase]);

  useEffect(() => {
    const baseFilters = toPaymentFilterVariable(searchFilters);
    const phaseType =
      projectPhase !== KEY_ALL
        ? [
          ...baseFilters,
          {
            _searchApprovalPhaseTypeId: {
              equalTo: Number(projectPhase),
            },
          },
        ]
        : baseFilters;

    const filtersFinalData =
      approvalType !== undefined
        ? [...phaseType, { approvalTypeId: { equalTo: approvalType } }]
        : phaseType;
    const variables: ApprovalsInfoVariables = {
      // after: showMore
      //   ? approvalSetupData?.approvals?.pageInfo.endCursor
      //   : undefined,
      filter:
        searchFilters?.length ||
          projectPhase !== KEY_ALL ||
          approvalType !== undefined
          ? ({
            and: filtersFinalData,
          } as ApprovalFilter)
          : undefined,
      orderBy: toOrderByVariable(sortOrderParam),
    };
    if (approvalType) getApprovalInfo({ variables });
  }, [
    approvalType,
    getApprovalInfo,
    projectPhase,
    searchFilters,
    sortOrderParam,
  ]);

  return (
    <ReactFlowProvider>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
        style={{
          maxWidth: `calc(100vw - ${isExpandedNav ? 500 : 320}px)`, // Adjust 20px based on scrollbar size
        }}
      >
        {/* Header  */}
        <Stack tokens={{ childrenGap: 20 }}>
          <Stack
            horizontal
            verticalAlign="center"
            horizontalAlign="space-between"
            tokens={{ childrenGap: 30 }}
            className={commonStyles.listTitleContainer}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 10 }}
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 7 }}
              >
                <Text variant="xLarge">Approval Studio</Text>
                <HelpIcon appLinkType="APPROVAL_WORKFLOW_DESIGNER" />
              </Stack>
              <ProjectPhase
                onChange={(data) => {
                  setProjectPhase(data);
                  setApprovalType(undefined);
                }}
                projectPhase={projectPhase}
              />
            </Stack>
            <ApprovalFlowDesigner
              currentApprovalTypeFilter={approvalType!}
              approvalsInfo={approvalsInfo}
              approvalId={id as string}
              setApprovalType={setApprovalType}
            />
          </Stack>

          <ApprovalTypeFilter
            value={approvalType}
            projectPhase={projectPhase}
            onChange={setApprovalType}
            data={data}
          />
        </Stack>
      </Stack>
      <Stack horizontal horizontalAlign="end" className={commonStyles.noSelect}>
        <Stack grow>
          <TooltipHost
            content={EXECUTION_PRIORITY_HINT_TEXT}
            id="priorityHint"
            directionalHint={DirectionalHint.topLeftEdge}
          >
            <Text style={{ paddingLeft: 20, fontWeight: 600 }} variant="large">
              Execution Priority
            </Text>
          </TooltipHost>
        </Stack>

        <Stack tokens={{ childrenGap: 20 }} horizontal>
          <Stack style={{ width: 100 }}>
            <Text style={{ fontWeight: 600 }} variant="large">
              Test cases
            </Text>
          </Stack>
          <Stack tokens={{ padding: '5px 20px' }} style={{ width: 200 }}>
            <CommandBarButton
              key="refresh"
              text="Refresh"
              iconProps={{ iconName: 'refresh' }}
              onClick={() => {
                refetch?.(variables);
                refreshApprovalTypes();
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack
        style={{ overflow: 'auto' }}
        tokens={{ padding: '0px 20px' }}
        className={commonStyles.noSelect}
      >
        <ReorderHierarchy
          approvalsInfo={approvalsInfo}
          loading={approvalInfoLoading}
          onRowOpen={(id) => {
            history.push(`/account-management/approval-setup?id=${id}`);
          }}
          refetch={refetch}
        />
      </Stack>
    </ReactFlowProvider>
  );
};
